import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import {
  BRANDED_QR_CODE_OPTIONS,
  LEGACY_BRANDED_QR_CODE_OPTIONS,
  useStyledQrCodeDataUrl,
} from "@src/appV2/lib/utils/useStyledQrCodeDataUrl";
import { useShiftFeedbackUrl } from "@src/appV2/ShiftFeedback/useShiftFeedbackUrl";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { z } from "zod";

interface TimeRange {
  startCard?: string;
  endCard?: string;
  start?: string;
  end?: string;
  isSkipped?: boolean;
  startImage?: string;
  endImage?: string;
}

const TTL_IN_SECONDS = 60 * 30; // 30 minutes

interface QRSignatureDataUrlProps {
  shiftId: string;
  clockInOut: TimeRange;
  lunchInOut?: TimeRange;
  breakAuthorizationReason?: string;
  facilityId: string;
  legacy: boolean;
  workerFirstName: string;
  isCaliforniaTimesheet: boolean;
}

export function useQRSignatureDataUrl(props: QRSignatureDataUrlProps) {
  const {
    data: feedbackUrlData,
    isLoading: isFeedbackUrlLoading,
    error: feedbackUrlError,
  } = useShiftFeedbackUrl(props);

  const {
    data: signoutPageLink = "",
    isLoading: isTokenLoading,
    error: tokenError,
  } = useQuery({
    queryKey: ["qr-signature-data", props],
    queryFn: async () => {
      const { qrData, url: feedbackUrl } = feedbackUrlData ?? {};
      if (!qrData || !feedbackUrl) {
        return undefined;
      }

      const {
        data: { shortUrl },
      } = await post({
        url: "/shortlinks",
        data: { url: feedbackUrl, ttlInSeconds: TTL_IN_SECONDS },
        responseSchema: z.object({ shortUrl: z.string() }),
      });

      return shortUrl;
    },
    enabled: isDefined(feedbackUrlData),
  });

  const {
    data: qrCodeDataUrl,
    isLoading: isQrCodeDataUrlLoading,
    error: qrCodeError,
  } = useStyledQrCodeDataUrl(
    {
      data: signoutPageLink,
      qrOptions: props.legacy ? LEGACY_BRANDED_QR_CODE_OPTIONS : BRANDED_QR_CODE_OPTIONS,
    },
    { enabled: !isEmpty(signoutPageLink), retry: 3 }
  );

  return {
    qrCodeDataUrl,
    isQrCodeDataUrlLoading: isQrCodeDataUrlLoading || isTokenLoading || isFeedbackUrlLoading,
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    error: feedbackUrlError || tokenError || qrCodeError,
  };
}
